import { IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { IUseDiscountController } from "../controllers/IUseDiscountController";
import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { BaseScalableView } from "by/gamefactory/cityversetycoon/scenes/common/BaseScalableView";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { ButtonWithScaleAnimation } from "by/gamefactory/cityversetycoon/scenes/common/components/ButtonWithScaleAnimation";
import { UIScrollBox } from "by/gamefactory/cityversetycoon/scenes/common/components/UIScrollBox";
import { BenefitItem } from "by/gamefactory/cityversetycoon/scenes/common/models/benefits/BenefitItem";
import { IBenefitsModel } from "by/gamefactory/cityversetycoon/scenes/common/models/benefits/IBenefitsModel";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { FadeScreen } from "by/gamefactory/cityversetycoon/scenes/game/components/FadeScreen";
import { IFocusable } from "by/gamefactory/cityversetycoon/scenes/layers/IFocusable";
import { PaymentResult } from "by/gamefactory/web3/PaymentResult";
import { MarketplaceBuildingItem } from "../../main/models/building/MarketplaceBuildingItem";
import { DiscountCouponContainer } from "../../mydiscount/components/DiscountCouponContainer";
import { DiscountCouponState } from "../../mydiscount/components/DiscountCouponState";
import { DiscountResourcePackage } from "../../mydiscount/resources/DiscountResourcePackage";
import { BuildingPriceInfoContainter } from "../components/BuildingPriceInfoContainter";
import { IUseDiscountViewModel } from "../model/IUseDiscountViewModel";
import { DynamicResourcesPackage } from "resources/DynamicResourcesPackage";
import { CancellationToken } from "async/CancellationToken";

export class UseDiscountPopupView extends BaseScalableView<PaymentResult> implements IFocusable {
    private static readonly BOTTOM_TEXT = 'YOUR SELECTED COUPONS ARE RESERVED FOR 15 MINUTES. PLEASE COMPLETE YOUR TRANSACTION\nWITHIN THIS TIMEFRAME TO APPLY THE DISCOUNT. UNCOMPLETED TRANSACTIONS WILL BE CANCELED,\n AND COUPONS WILL BE RESTORED TO YOUR ACCOUNT';

    private readonly cancellationToken: CancellationToken;
    private readonly commonResources: CommonResourcesPackage;
    private readonly benefitsDynamicResources: DynamicResourcesPackage;
    private readonly discountResources: DiscountResourcePackage;
    private readonly benefitModel: IBenefitsModel;
    private readonly useDiscountVIewModel: IUseDiscountViewModel;
    private readonly currentMpBuildingItem: MarketplaceBuildingItem;
    private readonly controller: IUseDiscountController;
    public readonly focusable: boolean = true;

    private fadeScreen: FadeScreen;
    private topLight: Sprite;
    private bottomLight: Sprite;
    private headerWhiteText: Text;
    private headerYellowText: Text;
    private additionalText: Text;
    private buildingPriceInfoContainer: BuildingPriceInfoContainter;
    private continueButton: ButtonWithScaleAnimation;
    private continueButtonText: Text;
    private scrollBox: UIScrollBox;
    private containers: Array<DiscountCouponContainer>;
    private closeButton: ButtonWithScaleAnimation;
    private bottomText: Text;

    public constructor(
        operationControls: PromiseCompletionSource<PaymentResult>,
        cancellationToken: CancellationToken,
        commonResources: CommonResourcesPackage,
        benefitsDynamicResources: DynamicResourcesPackage,
        discountResources: DiscountResourcePackage,
        canvasScaler: ICanvasScaler,
        benefitModel: IBenefitsModel,
        useDiscountVIewModel: IUseDiscountViewModel,
        currentMpBuildingItem: MarketplaceBuildingItem,
        controller: IUseDiscountController
    ) {
        super(operationControls, canvasScaler);
        this.cancellationToken = cancellationToken;
        this.commonResources = commonResources;
        this.benefitsDynamicResources = benefitsDynamicResources;
        this.discountResources = discountResources;
        this.benefitModel = benefitModel;
        this.useDiscountVIewModel = useDiscountVIewModel;
        this.currentMpBuildingItem = currentMpBuildingItem;
        this.controller = controller;
        this.containers = [];
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.cancellationToken.onCanceled.add(this.onCancellationRequested, this);
    }

    public async start(): Promise<void> {
        await this.show();
    }

    private build(): void {
        const width = this.canvasScaler.referenceWidth;
        const height = this.canvasScaler.referenceHeight;
        this.fadeScreen = new FadeScreen(0.1, 0, 0.7, width, height);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);
        this.topLight = new Sprite(this.discountResources.assets_views_discount_discount_top_light_png);
        this.addChild(this.topLight);
        this.bottomLight = new Sprite(this.discountResources.assets_views_discount_bottom_light_png);
        this.bottomLight.pivot.y = this.bottomLight.height;
        this.addChild(this.bottomLight);
        this.headerWhiteText = new Text(`USE DISCOUNT`, new HeaderTextStyle());
        this.headerWhiteText.skew._x = -0.087;
        this.headerWhiteText.angle = -5;
        this.addChild(this.headerWhiteText);
        this.headerYellowText = new Text(`COUPONS`, new HeaderTextStyle());
        this.headerYellowText.skew._x = -0.087;
        this.headerYellowText.angle = -5;
        this.headerYellowText.style.fill = ["#fee302", "#fe9223"];
        this.headerYellowText.style.fontSize = 193;
        this.addChild(this.headerYellowText);
        this.additionalText = new Text(`YOU CAN APPLY YOUR VALID COUPONS\nTO PURCHASE THIS BUILDING`, new HeaderTextStyle());
        this.additionalText.style.fontSize = 48;
        this.additionalText.style.lineHeight = 53;
        this.addChild(this.additionalText);
        this.buildingPriceInfoContainer = new BuildingPriceInfoContainter(this.discountResources, this.useDiscountVIewModel, this.currentMpBuildingItem);
        this.addChild(this.buildingPriceInfoContainer);
        this.continueButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_button_green_png);
        this.continueButton.onUp.add(this.onContinueClickHandle, this);
        this.addChild(this.continueButton);
        this.continueButtonText = new Text(`CONTINUE`, new ButtonTextStyle());
        this.continueButtonText.position.set(this.continueButton.width * 0.5 - this.continueButtonText.width * 0.5, this.continueButton.height * 0.5 - this.continueButtonText.height * 0.55);
        this.continueButton.addChild(this.continueButtonText);
        this.scrollBox = new UIScrollBox({
            width: width,
            height: 561,
            type: "vertical",
            elementsMargin: -80
        });
        this.addChild(this.scrollBox);
        this.benefitModel.benefits.value.forEach(item => this.discountContainerFactory(item))
        this.layoutContainers();
        if (this.benefitModel.benefits.value.length < 3) {
            this.scrollBox.setScrollable(false);
            this.scrollBox.setWheelScrollable(false);
        }
        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_x_png);
        this.closeButton.onUp.add(this.onCloseButtonClickHandle, this);
        this.addChild(this.closeButton);

        this.bottomText = new Text(UseDiscountPopupView.BOTTOM_TEXT, new BottomTextStyle());
        this.addChild(this.bottomText);
    }

    private async show(): Promise<void> {
        await this.fadeScreen.fadeIn();
    }

    private layout(width: number, height: number): void {
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.bottomLight.position.set(0, height);
        this.headerWhiteText.position.set(146, 189);
        this.headerYellowText.position.set(146, 303);
        this.additionalText.position.set(148, 485);
        this.buildingPriceInfoContainer.position.set(width * 0.5 - this.buildingPriceInfoContainer.width * 0.5, 1213);
        this.continueButton.position.set(width * 0.5 - this.continueButton.width * 0.5, 1405);
        this.scrollBox.position.set(0, 631);
        this.closeButton.position.set(709, 134);
        this.bottomText.position.set(width * 0.5 - this.bottomText.width * 0.5, 1613);
    };

    private onCancellationRequested(): void {
        this.operationControls.complete(PaymentResult.FAILURE);
        this.destroy();
    }

    private async onContinueClickHandle(): Promise<void> {
        const summaryDiscountPercentage = this.useDiscountVIewModel.summaryDiscountPercentage.value;
        if (this.currentMpBuildingItem.price.mon != null) {
            const appliedPrice = Math.max(Number(this.currentMpBuildingItem.price.mon * summaryDiscountPercentage), 0);
            const result = await this.controller.showMonPaymentProcessingPopup(this.currentMpBuildingItem.id, appliedPrice, this.useDiscountVIewModel.appliedCoupons.value);
            this.operationControls.complete(result);
        } else {
            const appliedPrice = Math.max(Number(this.currentMpBuildingItem.price.usd * summaryDiscountPercentage), 0);
            const result = await this.controller.showSelectPaymentMethodPopup(this.currentMpBuildingItem.id, this.cancellationToken, appliedPrice, this.useDiscountVIewModel.appliedCoupons.value);
            await this.fadeScreen.fadeOut();
            this.operationControls.complete(result);
        }
    }

    private async onCloseButtonClickHandle(): Promise<void> {
        await this.fadeScreen.fadeOut();
        this.operationControls.complete(null);
    }

    private discountContainerFactory(benefitItem: BenefitItem): DiscountCouponContainer {
        const container = new DiscountCouponContainer(this.benefitsDynamicResources, this.discountResources, DiscountCouponState.USED, benefitItem, this.useDiscountVIewModel);
        container.onCheckBoxDown.add(this.onCheckboxDownHandle, this);
        container.onCheckBoxUp.add(this.onCheckboxUpHandle, this);
        container.onCheckBoxOut.add(this.onCheckboxOutHandle, this);
        this.containers.push(container);
        this.scrollBox.addItem(container);
        return container;
    }

    private layoutContainers(): void {
        for (let i = 0; i < this.containers.length; i++) {
            const container = this.containers[i];
            container.position.x = this.scrollBox.width * 0.5 - container.width * 0.5;
        }
    }

    private onCheckboxUpHandle(): void {
        this.scrollBox.stopScrolling();
        this.scrollBox.eventMode = 'static';
        if (this.containers.length < 3) {
            this.scrollBox.setScrollable(false);
            this.scrollBox.setWheelScrollable(false);
        }
    }

    private onCheckboxDownHandle(): void {
        this.scrollBox.eventMode = 'auto';
    }

    private onCheckboxOutHandle(): void {
        this.scrollBox.stopScrolling();
        this.scrollBox.eventMode = 'static';
        if (this.containers.length < 3) {
            this.scrollBox.setScrollable(false);
            this.scrollBox.setWheelScrollable(false);
        }
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.cancellationToken.onCanceled.remove(this.onCancellationRequested, this);
        this.containers.forEach(container => {
            container.onCheckBoxDown.remove(this.onCheckboxDownHandle, this);
            container.onCheckBoxUp.remove(this.onCheckboxUpHandle, this);
            container.onCheckBoxOut.remove(this.onCheckboxOutHandle, this);
        });
        this.continueButton.onUp.remove(this.onContinueClickHandle, this);
        this.closeButton.onUp.remove(this.onCloseButtonClickHandle, this);
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 126
        }));
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAngle: 0.67,
            dropShadowAlpha: 0.5,
            dropShadowBlur: 5,
            dropShadowDistance: 2,
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 80,
            miterLimit: 0
        }));
    }
}

class BottomTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 26,
            fill: 'FFFFFF',
            align: 'center',
            lineHeight: 26
        });
    }
}
